import {
  BarElement,
  CategoryScale,
  ChartData,
  Chart as ChartJS,
  ChartOptions,
  Filler,
  Legend,
  LinearScale,
  ScriptableContext,
  Tooltip,
} from 'chart.js';
import { format } from 'date-fns';
import React from 'react';
import { Bar } from 'react-chartjs-2';
import IRRDataPoint from '../models/IRRDataPoint';
import ExtendedLiquid from '../services/ExtendedLiquid';
import { Palette } from 'Palette';
import { drawRightBorderPlugin } from './Util/ChartExtensions';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

interface IRRBarChartProps {
  data: IRRDataPoint[];
  tone: keyof typeof Palette;
  /**
   * Admin's Fund Management preview fills forecast bars with the app's
   * actual secondary button colour instead of leaving them as an outline —
   * two solid colours read better against the tile styling there than the
   * investor chart's outline treatment.
   */
  canEdit?: boolean;
}

export default function IRRBarChart({ data, tone, canEdit = false }: IRRBarChartProps) {
  const labels = data.map((d) => format(new Date(`${d.month}-01`), 'MMM yy'));
  const values = data.map((d) => d.discrete_value);
  const types: ('actual' | 'forecast')[] = data.map((d) => d.type);
  const actualColor = canEdit ? Palette[tone].buttonBg! : Palette[tone].graphLine!;
  const forecastColor = canEdit ? Palette.tone1.buttonBg! : Palette[tone].graphLine!;

  const chartData: ChartData<'bar'> = {
    labels,
    datasets: [
      {
        label: 'Discrete',
        data: values,
        borderWidth: (ctx: ScriptableContext<'bar'>): number => {
          const index = ctx.dataIndex;
          const pointType = types[index];
          return canEdit ? 0 : pointType === 'forecast' ? 2 : 0;
        },
        borderColor: (ctx: ScriptableContext<'bar'>): string | undefined => {
          const index = ctx.dataIndex;
          const pointType = types[index];
          if (canEdit) return undefined;
          return pointType === 'forecast' ? forecastColor : undefined; // Use a different color for forecast
        },
        backgroundColor: (ctx: ScriptableContext<'bar'>): string => {
          const index = ctx.dataIndex;
          const pointType = types[index];
          if (canEdit) return pointType === 'forecast' ? forecastColor : actualColor;
          return pointType === 'forecast' ? 'transparent' : actualColor;
        },
      },
    ],
  };

  const options: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'nearest' as const, // ✅ or "x", "y", "point", etc. — just not "index"
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: (context: import('chart.js').TooltipItem<'bar'>) => {
            const value = parseInt((context.raw as number).toFixed(0));
            const type = types[context.dataIndex];
            const formattedValue = ExtendedLiquid.currencyMillions(
              value as number,
              true,
              true,
              true,
              true,
            );

            return `${type === 'forecast' ? 'Forecast' : 'Actual'}: ${formattedValue}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
        },
      },
      y: {
        beginAtZero: false,
        ticks: {
          callback: (value: number | string) => {
            return ExtendedLiquid.currencyMillions(value as number, true, true, true);
          },
        },
      },
    },
  };

  return (
    <>
      <div style={{ width: '100%', height: 500 }} className="react-IRRBarChart">
        <Bar data={chartData} options={options} plugins={[drawRightBorderPlugin]} />
      </div>
    </>
  );
}
